/**
 * @module utils/computeInitialBase
 * @description 图表设置 BASE 初始值计算（纯函数）— 进页初始化 / QB 重置与面板打开解耦
 */

import { ChartType, ConfigChartType, type TChartType } from '@dbl/core/chart';
import {
  ChartCalculateMode,
  normalizeDataLabelConfig,
  type TChartCalculateMode,
} from '@dbl/core/chartEditor';
import { SCALAR_SETTING_DEFAULTS } from '../manifest/settingsSchema';
import { CHART_SWITCH_DEFAULT } from '../types/settings/chartSwitch';
import { DEFAULT_WORDCLOUD_COUNT } from './constants';
import { compatMixedChartFormState } from '../settingGroups/common/DualAxis/axisMetricUtils';
import {
  buildDefaultCrosstabFromQB,
  normalizeCrosstabConfig,
} from './crosstab';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { detectCompareTime } from '../linkage/qbFields/qbLinkage/detectChanges/detectCompareTime';
import {
  buildColumnSortFromQB,
  type IBuildColumnSortFromQBInput,
} from '../linkage/qbFields/qbLinkage/handlers/columnSort/fromQB/buildFromQB';
import { applyCompareTimeToColumnSort } from '../linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/compareTimeUserFilter';
import { reconcileColumnSortWithQB } from '../linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/syncOps';

/** 计算初始 BASE 的输入 */
export interface IComputeInitialBaseInput {
  chartType: TChartType;
  saveColumnInfos: unknown;
  columnDimensions: unknown[];
  columnMetrics: unknown[];
  columnFilters?: unknown[];
  mode: TChartCalculateMode;
}

interface IRawConfigInput {
  chartsCfgSave: Record<string, Record<string, unknown>> | undefined;
  columnDimensions: unknown[];
  columnMetrics: unknown[];
  columnFilters?: unknown[];
  mode: TChartCalculateMode;
}

export function extractFromChartsCfgSave(
  chartsCfgSave: Record<string, Record<string, unknown>> | undefined,
  chartType: TChartType,
): Record<string, unknown> {
  const typeKey = chartType as string;
  const wrapper = chartsCfgSave?.[typeKey];
  if (!wrapper?.customizeSetting) return {};

  const storageKey = ConfigChartType[chartType];
  const inner = (wrapper.customizeSetting as Record<string, unknown>)?.[storageKey];
  if (inner && typeof inner === 'object') {
    return { ...(inner as Record<string, unknown>) };
  }
  return {};
}

export function selectConfigSource(
  input: IRawConfigInput,
  chartType: TChartType,
): Record<string, unknown> {
  const { chartsCfgSave } = input;

  return extractFromChartsCfgSave(chartsCfgSave, chartType);
}

/**
 * C1 读兼容：旧 showValues → 新数据标签字段（委托共用归一化）
 */
export function compatShowValues(
  config: Record<string, unknown>,
  chartType: TChartType,
): void {
  normalizeDataLabelConfig(config, chartType);
}

export function compatChartSorts(config: Record<string, unknown>): void {
  const custom = config.customMetricSort;
  const chart = config.chartSorts;
  const sorts = (Array.isArray(custom) ? custom : Array.isArray(chart) ? chart : []) as unknown[];
  // eslint-disable-next-line no-param-reassign
  config.chartSorts = sorts;
  // eslint-disable-next-line no-param-reassign
  config.customMetricSort = sorts;
}

const REVERSE_SORT_BY_KEY: Record<string, string> = {
  metric: 'customMetricSort',
  default: 'defaultSort',
};

export function compatSortBy(config: Record<string, unknown>): void {
  const rawSortBy = config.sortBy as string | undefined;
  if (rawSortBy && REVERSE_SORT_BY_KEY[rawSortBy]) {
    // eslint-disable-next-line no-param-reassign
    config.sortBy = REVERSE_SORT_BY_KEY[rawSortBy];
  }
}

export function compatTopNPrefix(config: Record<string, unknown>): void {
  if (config.xAxisDynamicState !== undefined) {
    // eslint-disable-next-line no-param-reassign
    config.dynamicState = config.xAxisDynamicState;
    // eslint-disable-next-line no-param-reassign
    config.count = config.xAxisCount;
    // eslint-disable-next-line no-param-reassign
    config.showOthers = config.xAxisShowOthers;
  }
}

/** 剔除 column_sort 中已不在当前 QB 的列（持久化配置可能残留旧字段） */
export function compatColumnSort(
  config: Record<string, unknown>,
  input: IRawConfigInput,
): void {
  const raw = config.column_sort;
  if (!Array.isArray(raw) || raw.length === 0) {
    return;
  }
  const compareTime = detectCompareTime(input.columnFilters);
  const qbInput = {
    columnDimensions: input.columnDimensions,
    columnMetrics: input.columnMetrics,
    columnFilters: input.columnFilters,
    columnPercentage: Array.isArray(config.column_percentage)
      ? (config.column_percentage as Array<{ metric: string; table_code?: string }>)
      : undefined,
    dimensionPercentage:
      config.dimension_percentage &&
      typeof config.dimension_percentage === 'object' &&
      !Array.isArray(config.dimension_percentage)
        ? (config.dimension_percentage as IBuildColumnSortFromQBInput['dimensionPercentage'])
        : undefined,
    compareTime,
  };
  const canonical = buildColumnSortFromQB(qbInput);
  let next = reconcileColumnSortWithQB(
    raw as IColumnSortItem[],
    qbInput,
    canonical,
  );
  next = applyCompareTimeToColumnSort(
    next,
    compareTime,
    input.columnDimensions || [],
    input.columnFilters,
  );
  // eslint-disable-next-line no-param-reassign
  config.column_sort = next;
}

/**
 * chart_switch 写入 base 的可变副本（Schema 默认为冻结引用，避免进 base 后被原地修改）
 */
function cloneChartSwitchDefaultForBase(): Record<string, unknown> {
  return {
    show: CHART_SWITCH_DEFAULT.show,
    chartTypes: [...CHART_SWITCH_DEFAULT.chartTypes],
  };
}

export function applyDefaults(
  config: Record<string, unknown>,
  input: IRawConfigInput,
  chartType?: TChartType,
): void {
  // C5：缺失时用 SCALAR_SETTING_DEFAULTS；已有值仍用 !! 兼容历史 falsey
  // eslint-disable-next-line no-param-reassign
  config.dualYAxisEnabled =
    config.dualYAxisEnabled === undefined
      ? SCALAR_SETTING_DEFAULTS.dualYAxisEnabled
      : !!config.dualYAxisEnabled;

  if (chartType === ChartType.MixedChart) {
    compatMixedChartFormState(config, input.columnMetrics);
  }

  if (chartType !== ChartType.MixedChart) {
    const normalizeAxisMetrics = (axis: Record<string, unknown> | undefined): void => {
      if (
        axis &&
        !Array.isArray(axis.metrics) &&
        typeof axis.metric === 'string' &&
        axis.metric
      ) {
        axis.metrics = [{ metric: axis.metric, tableCode: 't0' }];
      }
    };
    normalizeAxisMetrics(config.primaryYAxis as Record<string, unknown> | undefined);
    normalizeAxisMetrics(config.secondYAxis as Record<string, unknown> | undefined);
  }

  // C7：accuracy 尚未纳入 SCALAR_SETTING_DEFAULTS，暂保留字面量（阶段 3.1 对账项）
  if (config.accuracy === undefined) {
    // eslint-disable-next-line no-param-reassign
    config.accuracy = 2;
  }

  // C8：与 STRUCTURED_SETTING_DEFAULTS.chart_switch 语义一致，base 用可变副本
  if (config.chart_switch === undefined) {
    // eslint-disable-next-line no-param-reassign
    config.chart_switch = cloneChartSwitchDefaultForBase();
  }

  // eslint-disable-next-line no-param-reassign
  config.merge_same_cells = config.merge_same_cells ?? { enabled: false };

  if (!config.crosstab && input.columnDimensions.length > 0) {
    // eslint-disable-next-line no-param-reassign
    config.crosstab = buildDefaultCrosstabFromQB(
      input.columnDimensions,
      input.columnMetrics,
    );
  } else if (config.crosstab) {
    const normalized = normalizeCrosstabConfig(
      config.crosstab,
      input.columnDimensions,
      input.columnMetrics,
    );
    if (normalized) {
      // eslint-disable-next-line no-param-reassign
      config.crosstab = normalized;
    }
  }

  // 词云 count：展示默认 30，与 TopN 的 SCALAR count（20）不同，保留专用常量
  if (chartType === ChartType.WordCloud && config.count === undefined) {
    // eslint-disable-next-line no-param-reassign
    config.count = DEFAULT_WORDCLOUD_COUNT;
  }

  if (Array.isArray(config.xAxisValues)) {
    // eslint-disable-next-line no-param-reassign
    config.xAxisValues = (config.xAxisValues as string[])
      .slice(0, 1000)
      .map(v => {
        if (v === 'custom_undefined') return '';
        if (v === '[NULL]') return 'null';
        return v;
      });
  }
}

/**
 * 计算应写入 Context BASE 的完整初始配置
 */
export function computeInitialBase(input: IComputeInitialBaseInput): Record<string, unknown> {
  const scObj = input.saveColumnInfos as Record<string, unknown> | undefined;
  const raw: IRawConfigInput = {
    chartsCfgSave: scObj?.chartsCfgSave as
      | Record<string, Record<string, unknown>>
      | undefined,
    columnDimensions: input.columnDimensions || [],
    columnMetrics: input.columnMetrics || [],
    columnFilters: input.columnFilters,
    mode: input.mode,
  };

  const config = selectConfigSource(raw, input.chartType);

  compatShowValues(config, input.chartType);
  compatChartSorts(config);
  compatSortBy(config);
  compatTopNPrefix(config);
  applyDefaults(config, raw, input.chartType);
  compatColumnSort(config, raw);

  return config;
}

/** 用于避免重复 SET_BASE 的轻量签名 */
export function buildBaseInitSignature(input: IComputeInitialBaseInput): string {
  const scObj = input.saveColumnInfos as Record<string, unknown> | undefined;
  const saveKey = scObj?.chartsCfgSave ? '1' : '0';
  return [
    input.chartType,
    input.mode,
    saveKey,
    input.columnDimensions.length,
    input.columnMetrics.length,
  ].join('|');
}

/** save_column_infos 是否含可读的 chartsCfgSave */
export function hasChartsCfgSave(saveColumnInfos: unknown): boolean {
  const scObj = saveColumnInfos as Record<string, unknown> | undefined;
  return (
    !!scObj?.chartsCfgSave &&
    Object.keys(scObj.chartsCfgSave as object).length > 0
  );
}

/** bridge 是否可从 DVA 计算并写入 BASE（需已有 chartsCfgSave） */
export function canInitializeBase(input: IComputeInitialBaseInput): boolean {
  return hasChartsCfgSave(input.saveColumnInfos);
}

